<template>
  <div
    :class="[
      'flex gap-3 p-4 cursor-pointer transition-all rounded-lg',
      isUnread
        ? 'bg-gray-800/90 border border-gray-700/50 shadow-sm'
        : 'bg-gray-900/60 border border-gray-800/30',
      'hover:bg-gray-800 hover:border-gray-700 hover:shadow-md'
    ]"
    @click="handleClick">
    <!-- Icon -->
    <div
      :class="[
        'flex-shrink-0 flex items-center justify-center size-10 rounded-full',
        iconBgClass
      ]">
      <fa-icon :icon="iconName" :class="['text-sm', iconColorClass]" />
    </div>

    <!-- Content -->
    <div class="flex-1 min-w-0">
      <div class="flex gap-2 justify-between items-start">
        <p
          :class="[
            'text-sm leading-snug',
            isUnread ? 'text-white font-semibold' : 'text-gray-200'
          ]">
          {{ notification.title }}
        </p>
        <!-- Unread dot -->
        <div
          v-if="isUnread && showUnreadIndicator"
          class="flex-shrink-0 size-2.5 rounded-full bg-rose-500 shadow-sm shadow-rose-500/50 mt-1.5" />
      </div>

      <p
        v-if="notification.body"
        class="mt-1 text-xs text-gray-300 line-clamp-2">
        {{ notification.body }}
      </p>

      <p class="text-xs text-gray-400 mt-1.5 font-medium">
        {{ timeAgo }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import {
  useStoreNotifications,
  NOTIFICATION_TYPES
} from '@/stores/storeNotifications'
import { useStoreModals } from '@/stores/storeModals'
import { formatTimeAgo } from '@/use/useFormatDate'

const props = defineProps({
  notification: {
    type: Object,
    required: true
  },
  showUnreadIndicator: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['click'])

const router = useRouter()
const storeNotifications = useStoreNotifications()

// Check if notification is unread
const isUnread = computed(() => !props.notification.read_at)

// Format time ago
const timeAgo = computed(() => {
  return formatTimeAgo(props.notification.created_at)
})

// Get icon based on notification type
const iconConfig = computed(() => {
  const type = props.notification.type

  switch (type) {
    // Social notifications
    case NOTIFICATION_TYPES.MESSAGE_RECEIVED:
      return {
        icon: ['fas', 'paper-plane'],
        bg: 'bg-indigo-500/30',
        color: 'text-indigo-300'
      }
    case NOTIFICATION_TYPES.COMMENT_RECEIVED:
      return {
        icon: ['fas', 'comment'],
        bg: 'bg-cyan-500/30',
        color: 'text-cyan-300'
      }
    case NOTIFICATION_TYPES.COMMENT_LIKE:
      return {
        icon: ['fas', 'heart'],
        bg: 'bg-rose-500/30',
        color: 'text-rose-300'
      }
    case NOTIFICATION_TYPES.REACTION_RECEIVED:
      return {
        icon: ['fas', 'heart'],
        bg: 'bg-pink-500/30',
        color: 'text-pink-300'
      }
    case NOTIFICATION_TYPES.FOLLOW_RECEIVED:
      return {
        icon: ['fas', 'user-plus'],
        bg: 'bg-purple-500/30',
        color: 'text-purple-300'
      }
    // Club notifications
    case NOTIFICATION_TYPES.CLUB_JOIN_REQUEST:
      return {
        icon: ['fas', 'user-plus'],
        bg: 'bg-blue-500/30',
        color: 'text-blue-300'
      }
    case NOTIFICATION_TYPES.CLUB_REQUEST_APPROVED:
      return {
        icon: ['fas', 'check-circle'],
        bg: 'bg-green-500/30',
        color: 'text-green-300'
      }
    case NOTIFICATION_TYPES.CLUB_REQUEST_REJECTED:
      return {
        icon: ['fas', 'times-circle'],
        bg: 'bg-red-500/30',
        color: 'text-red-300'
      }
    case NOTIFICATION_TYPES.CLUB_PROMOTED:
      return {
        icon: ['fas', 'arrow-up'],
        bg: 'bg-yellow-500/30',
        color: 'text-yellow-300'
      }
    case NOTIFICATION_TYPES.CLUB_DEMOTED:
      return {
        icon: ['fas', 'arrow-down'],
        bg: 'bg-orange-500/30',
        color: 'text-orange-300'
      }
    // Collaborative list notifications
    case NOTIFICATION_TYPES.LIST_INVITE_RECEIVED:
      return {
        icon: ['fas', 'envelope'],
        bg: 'bg-indigo-500/30',
        color: 'text-indigo-300'
      }
    case NOTIFICATION_TYPES.LIST_INVITE_ACCEPTED:
      return {
        icon: ['fas', 'check-circle'],
        bg: 'bg-green-500/30',
        color: 'text-green-300'
      }
    case NOTIFICATION_TYPES.LIST_INVITE_DECLINED:
      return {
        icon: ['fas', 'times-circle'],
        bg: 'bg-orange-500/30',
        color: 'text-orange-300'
      }
    case NOTIFICATION_TYPES.LIST_JOIN_REQUEST:
      return {
        icon: ['fas', 'user-plus'],
        bg: 'bg-blue-500/30',
        color: 'text-blue-300'
      }
    case NOTIFICATION_TYPES.LIST_REQUEST_APPROVED:
      return {
        icon: ['fas', 'check-circle'],
        bg: 'bg-green-500/30',
        color: 'text-green-300'
      }
    case NOTIFICATION_TYPES.LIST_REQUEST_REJECTED:
      return {
        icon: ['fas', 'times-circle'],
        bg: 'bg-red-500/30',
        color: 'text-red-300'
      }
    case NOTIFICATION_TYPES.LIST_MEMBER_JOINED:
      return {
        icon: ['fas', 'user-group'],
        bg: 'bg-purple-500/30',
        color: 'text-purple-300'
      }
    case NOTIFICATION_TYPES.LIST_MEMBER_LEFT:
      return {
        icon: ['fas', 'user-minus'],
        bg: 'bg-orange-500/30',
        color: 'text-orange-300'
      }
    default:
      return {
        icon: ['fas', 'bell'],
        bg: 'bg-gray-700/50',
        color: 'text-gray-300'
      }
  }
})

const iconName = computed(() => iconConfig.value.icon)
const iconBgClass = computed(() => iconConfig.value.bg)
const iconColorClass = computed(() => iconConfig.value.color)

// Handle click - mark as read and navigate
const handleClick = async () => {
  // Mark as read if unread
  if (isUnread.value) {
    await storeNotifications.markAsRead(props.notification.id)
  }

  // Emit click for parent handling
  emit('click', props.notification)

  // Navigate based on notification data
  const data = props.notification.data || {}

  if (data.conversation_id) {
    const storeModals = useStoreModals()
    storeModals.openModal({
      type: 'Messages',
      props: { conversationId: data.conversation_id }
    })
  } else if (data.review_id) {
    router.push({ name: 'WebReviewsSingle', params: { id: data.review_id } })
  } else if (data.follower_id) {
    router.push({ name: 'WebActivity' })
  } else if (data.list_id) {
    // List-related notification - navigate to the list
    router.push({ name: 'WebList', params: { id: data.list_id } })
  } else if (data.club_slug) {
    router.push({ name: 'WebClubDetail', params: { slug: data.club_slug } })
  } else if (data.club_id) {
    // If we have club_id but not slug, go to clubs page
    router.push({ name: 'WebClubs' })
  }
}
</script>
